<script setup lang="ts">
import { ref } from 'vue'
import { motion, AnimatePresence } from 'motion-v'
import { useAuditStore } from '@/stores/audit'
import AuditReport from '@/components/AuditReport.vue'
import portraitUrl from '@/assets/dallan-baker.webp'

const store = useAuditStore()
const domain = ref('')
const email = ref('')

async function onSubmit() {
  await store.submit(domain.value, email.value)
}
</script>

<template>
  <section class="hero">
    <div class="hero-grid">
      <motion.div
        class="hero-col hero-col-left"
        :initial="{ opacity: 0, y: 14 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.6, ease: 'easeOut' }"
      >
        <p class="hero-eyebrow eyebrow">Castle Country Digital — Carbon &amp; Emery County, Utah</p>

        <h1 class="hero-title display">
          I build websites for Castle Country,
          <em>and I keep them working.</em>
        </h1>

        <p class="hero-lede">
          I build and maintain websites for shops, clinics, contractors, and other outfits across
          Carbon and Emery County. Run the free security check on your own address — no signup, and
          the report renders right here for anyone, in about fifteen seconds.
        </p>

        <figure class="hero-portrait">
          <img
            class="portrait-frame"
            :src="portraitUrl"
            width="480"
            height="600"
            loading="lazy"
            decoding="async"
            alt="Dallan Baker, proprietor of Castle Country Digital"
          />
          <figcaption class="portrait-caption">Dallan Baker — Castle Dale, Utah</figcaption>
        </figure>

        <p class="hero-note drop-cap">
          You deal with me, not a rotating account manager. When you call the number below, I'm the
          one who picks up, and whatever the job takes, it's on me to get it right. When I quote a
          job, the invoice says exactly what you're paying for.
          <span class="hero-note-sig">— Dallan Baker</span>
        </p>
      </motion.div>

      <motion.div
        class="hero-col hero-col-right"
        :initial="{ opacity: 0, y: 18 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.6, delay: 0.12, ease: 'easeOut' }"
      >
        <div class="audit-panel">
          <header class="audit-panel-head">
            <span class="section-marker">Free · no signup</span>
            <h2 class="audit-panel-title">Try it on your own site.</h2>
            <p class="audit-panel-lede">
              Five checks against any domain — yours, or a competitor's.
            </p>
          </header>

          <form class="audit-inline-form" @submit.prevent="onSubmit">
            <div class="audit-inline-row">
              <label class="visually-hidden" for="hero-domain">Your website</label>
              <input
                id="hero-domain"
                v-model="domain"
                type="text"
                placeholder="your-business.com"
                autocomplete="url"
                required
                :disabled="store.status === 'running'"
              />
              <label class="visually-hidden" for="hero-email">Your email</label>
              <input
                id="hero-email"
                v-model="email"
                type="email"
                placeholder="you@example.com"
                autocomplete="email"
                :disabled="store.status === 'running'"
              />
              <button type="submit" class="audit-submit" :disabled="store.status === 'running'">
                {{ store.status === 'running' ? 'Checking…' : 'Run the check' }}
              </button>
            </div>
          </form>

          <p v-if="store.status === 'error'" class="audit-error" role="alert">
            {{ store.error }}
          </p>

          <AnimatePresence>
            <AuditReport
              v-if="store.status === 'success' && store.report"
              :key="store.report.host + store.report.timestamp"
              :report="store.report"
              :audit-id="store.auditId"
            />
          </AnimatePresence>

          <div v-if="store.status === 'idle' || store.status === 'running'" class="audit-frame">
            <div class="audit-frame-head">
              <span class="audit-frame-bar audit-frame-bar-wide"></span>
              <span class="audit-frame-bar audit-frame-bar-score"></span>
            </div>
            <div class="audit-frame-row"></div>
            <div class="audit-frame-row"></div>
            <div class="audit-frame-row"></div>
            <p class="audit-frame-note">Your report appears here.</p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  padding: 4rem 0 3.5rem;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: start;
}

.hero-col {
  min-width: 0;
}

.hero-eyebrow {
  margin: 0;
}

.hero-title {
  margin: 0.5rem 0 0;
  font-size: clamp(2.2rem, 5vw, 3.4rem);
  color: var(--text);
}

.hero-lede {
  margin: 1.1rem 0 0;
  max-width: 34em;
  font-family: var(--font-body);
  font-size: 1.08rem;
  line-height: 1.65;
  color: var(--text-muted);
  font-variation-settings: 'opsz' 18;
}

/* Portrait placeholder */
.hero-portrait {
  margin: 2rem 0 0;
  display: flex;
  align-items: center;
  gap: 1.1rem;
}

.portrait-frame {
  width: 10.5rem;
  /* The width/height attributes reserve layout space against CLS, but their
     height would otherwise win over aspect-ratio and render this 168x600. */
  height: auto;
  aspect-ratio: 4 / 5;
  flex-shrink: 0;
  object-fit: cover;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-sm);
}

.portrait-caption {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-muted);
}

/* Proprietor's note — the Weekly drop-cap borrow, applied to plain text only */
.hero-note {
  margin: 1.5rem 0 0;
  max-width: 34em;
  font-size: 1.05rem;
  line-height: 1.65;
  color: var(--text);
}

.hero-note-sig {
  font-family: var(--font-mono);
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  color: var(--text-muted);
  white-space: nowrap;
}

/* Audit panel — the working centrepiece */
.audit-panel {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.75rem;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.audit-panel-head {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.audit-panel-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.4rem;
  font-weight: 500;
  color: var(--text);
  font-variation-settings: 'opsz' 36;
}

.audit-panel-lede {
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.55;
  color: var(--text-muted);
}

.audit-inline-row {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.audit-inline-row input {
  flex: 1 1 11rem;
  min-width: 0;
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 0.7rem 0.9rem;
  font-size: 0.95rem;
  font-family: inherit;
}
.audit-inline-row input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-tint);
}
.audit-inline-row input:disabled {
  opacity: 0.6;
}

.audit-submit {
  flex: 1 1 100%;
  padding: 0.75rem 1.2rem;
  background: var(--accent);
  color: var(--on-accent);
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 0.98rem;
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.2s ease;
}
.audit-submit:hover:not(:disabled) {
  background: var(--accent-bright);
}
.audit-submit:disabled {
  opacity: 0.65;
  cursor: wait;
}

.audit-error {
  margin: 0;
  background: var(--fail-tint);
  border: 1px solid var(--fail);
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  font-size: 0.92rem;
  color: var(--text);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Idle / running frame — no invented check content, just structure */
.audit-frame {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface-2);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.audit-frame-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid var(--border);
}

.audit-frame-bar {
  display: block;
  height: 0.6rem;
  border-radius: 999px;
  background: var(--surface-3);
}
.audit-frame-bar-wide {
  width: 45%;
}
.audit-frame-bar-score {
  width: 2.5rem;
}

.audit-frame-row {
  height: 0.6rem;
  border-radius: 999px;
  background: var(--surface-3);
  width: 100%;
}
.audit-frame-row:nth-child(3) {
  width: 88%;
}
.audit-frame-row:nth-child(4) {
  width: 70%;
}

.audit-frame-note {
  margin: 0.2rem 0 0;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-faint);
}

@media (min-width: 880px) {
  .hero-grid {
    grid-template-columns: 1.05fr 0.95fr;
    gap: 3.5rem;
  }
}

@media (max-width: 720px) {
  .hero {
    padding: 3rem 0 2.5rem;
  }
  .hero-title {
    font-size: clamp(2rem, 8vw, 2.8rem);
  }
  .audit-inline-row input {
    flex-basis: 100%;
  }
}
</style>
